import {
  configApiRef,
  createFrontendModule,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import searchPlugin from '@backstage/plugin-search/alpha';
import {
  SearchContextProvider,
  searchApiRef,
} from '@backstage/plugin-search-react';
import {
  SearchFilterResultTypeBlueprint,
  SearchResultListItemBlueprint,
} from '@backstage/plugin-search-react/alpha';
import { SearchClient } from './api/SearchApi';
import { ConfluenceSearchIcon } from './components/confluence/ConfluenceSearchIcon';

/**
 * Replaces the core search client (`api:search`). The Xelerate client strips
 * the scheme from URL search terms, expands the `confluence` result type
 * into the Confluence shard types the backend currently serves, and adds the
 * Quality Seal status to catalog results when `features.enable-quality-seal`
 * is on.
 */
export const searchApi = searchPlugin.getExtension('api:search').override({
  params: defineParams =>
    defineParams({
      api: searchApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, fetchApi, configApi }) =>
        new SearchClient({ discoveryApi, fetchApi, configApi }),
    }),
});

/**
 * Replaces the core search page (`page:search`) with the Xelerate search page.
 * The page keeps the core path and route ref. Results render through the
 * `search-result-list-item` extensions attached to the page, so each plugin
 * renders its own result type; the Xelerate result card is the fallback.
 */
export const searchPage = searchPlugin.getExtension('page:search').override({
  factory: (originalFactory, { inputs }) =>
    originalFactory({
      params: {
        loader: async () => {
          const resultItems = inputs.items.map(item =>
            item.get(SearchResultListItemBlueprint.dataRefs.item),
          );
          const { SearchPage } = await import('./components/SearchPage');
          return (
            <SearchContextProvider>
              <SearchPage resultItems={resultItems} />
            </SearchContextProvider>
          );
        },
      },
    }),
});

/**
 * Module for the core `search` plugin: install it next to
 * `@backstage/plugin-search/alpha` (installed by package discovery).
 */
export const searchOverridesModule = createFrontendModule({
  pluginId: 'search',
  extensions: [searchApi, searchPage],
});

/** Renders Confluence search results on the search page. */
export const confluenceSearchResultListItem =
  SearchResultListItemBlueprint.make({
    name: 'search-result',
    params: {
      component: () =>
        import('./components/confluence/ConfluenceSearchResultListItem').then(
          m => m.ConfluenceSearchResultListItem,
        ),
      predicate: result => result.type === 'confluence',
    },
  });

/** The Confluence result type for the core search page's result type filter. */
export const confluenceSearchFilterResultType =
  SearchFilterResultTypeBlueprint.make({
    name: 'confluence-results-type',
    params: {
      value: 'confluence',
      name: 'Confluence',
      icon: <ConfluenceSearchIcon />,
    },
  });

/**
 * The Confluence search plugin: the result list item and result type for
 * `confluence` search results, and the `confluence-search` feature flag.
 */
export const confluencePlugin = createFrontendPlugin({
  pluginId: 'confluence',
  extensions: [
    confluenceSearchResultListItem,
    confluenceSearchFilterResultType,
  ],
});
